/* eslint-disable no-magic-numbers , complexity */
import clsx from 'clsx';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ForecastListItem } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { FormattedMessage, useIntl } from '@futrli/localisation';

import Icon from 'carbon-react/lib/components/icon';

import { FinData, FinFieldOptions, PeriodType, TableStyle, DataField } from '../../types';
import { isBeforeCurrentPeriod, isBeforeCurrentPeriodDate } from '../../utils-dates';
import { TABLE_COLUMN_WIDTH, TABLE_FIRST_COLUMN_WIDTH } from '../../constants';
import { EditableTitles } from '../editable-titles/editable-titles';
import { useI18nConstants } from '../../use-i18n-constants';

import { useRowHeights } from './hooks/use-row-heights';
import { useAllTableStyles } from './hooks/use-all-table-styles';
import { useTableHeaders } from './hooks/use-table-headers';
import { calculatePagination, TableField } from './utils/pagination';
import { buildTablesByPage, TableColumn } from './utils/table-data-builder';

import styles from './report-table.scss';

const TITLES_SIZE = 50;

interface Props {
  title: string;
  subTitle: string;
  data: (FinData | undefined)[];
  fields: TableField[];
  rowsOptions: { ref: string; options: FinFieldOptions | undefined }[];
  headerRowStyle: TableStyle;
  forecastsRowStyle: TableStyle;
  normalRowStyle: TableStyle;
  categoriesRowStyle: TableStyle;
  groupsRowStyle: TableStyle;
  accountsRowStyle: TableStyle;
  assumptionsRowStyle: TableStyle;
  formulasRowStyle: TableStyle;
  driverGroupsRowStyle: TableStyle;
  driversRowStyle: TableStyle;
  currency: string;
  initialSpace: number;
  maxSpace: number;
  breakSpace?: number;
  doNotBreak?: boolean;
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[];
  hasComparisonData?: boolean;
  tableWidth: number;
  periodType: PeriodType;
  allFinFieldsArray: DataField[];
  shouldPaginate?: boolean;
  firstColumnWidth?: number;
  columnWidth?: number;
  userZoom?: number;
  collapsedFields: string[] | undefined;
  updateCollapsedFields: (fields: string[]) => void;
  viewOnly?: boolean;
}

export const PaginatedReportTable = ({
  title,
  subTitle,
  data,
  fields,
  rowsOptions,
  currency,
  headerRowStyle,
  forecastsRowStyle,
  normalRowStyle,
  categoriesRowStyle,
  groupsRowStyle,
  accountsRowStyle,
  assumptionsRowStyle,
  formulasRowStyle,
  driverGroupsRowStyle,
  driversRowStyle,
  maxSpace,
  breakSpace = 0,
  initialSpace,
  doNotBreak,
  selectedForecasts,
  hasComparisonData,
  periodType,
  allFinFieldsArray,
  shouldPaginate,
  firstColumnWidth = TABLE_FIRST_COLUMN_WIDTH,
  columnWidth = TABLE_COLUMN_WIDTH,
  userZoom = 1,
  collapsedFields = [],
  updateCollapsedFields,
  viewOnly
}: Props) => {
  const intl = useIntl();
  const { roundAndFormatValue } = useI18nConstants();
  const [fieldsByPage, setFieldsByPage] = useState<TableField[][]>();
  const [tablesByPage, setTablesByPage] = useState<
    { table: TableColumn[]; startColumn: number; endColumn: number }[][]
  >([]);
  const [topMargins, setTopMargins] = useState<number[]>([]);
  const [topPadding, setTopPadding] = useState(0);
  const [maxColumnsPerTable, setMaxColumnsPerTable] = useState(0);
  const [extraWidthPerPx, setExtraWidthPerPx] = useState(0);
  const tablesContainerRef = useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = useState(tablesContainerRef.current?.offsetWidth || 0);
  const [collapsedFieldsMemo, setCollapsedFieldsMemo] = useState(collapsedFields);

  const hasData = useMemo(() => data?.length && data[0] && data[0]?.periods?.length, [data]);

  const columnsNumber = useMemo(
    () =>
      selectedForecasts.length > 1 && hasComparisonData && data[0]?.columns?.length
        ? data[0]?.columns?.length
        : (data[0]?.periods?.length || 0) * selectedForecasts.length,
    [data, hasComparisonData, selectedForecasts.length]
  );

  // --- Layout measurement ---
  useLayoutEffect(() => {
    const container = tablesContainerRef.current;
    const handleResize = () => {
      setTableWidth(container?.offsetWidth || 0);
    };
    container?.addEventListener('resize', handleResize);
    handleResize();
    return () => container?.removeEventListener('resize', handleResize);
  }, []);

  // --- Zoom ---
  const zoom = useMemo(() => {
    if (shouldPaginate || doNotBreak) {
      return userZoom;
    }
    if (!tableWidth) {
      return 1;
    }
    const cellWidth = (tableWidth - firstColumnWidth) / columnsNumber;
    return cellWidth < columnWidth ? cellWidth / columnWidth : 1;
  }, [tableWidth, columnsNumber, columnWidth, firstColumnWidth, shouldPaginate, userZoom, doNotBreak]);

  // --- Styles ---
  const { headerStyles, forecastsStyles, stylesPerType, rowStylesPerType } = useAllTableStyles({
    headerRowStyle,
    forecastsRowStyle,
    categoriesRowStyle,
    groupsRowStyle,
    accountsRowStyle,
    assumptionsRowStyle,
    formulasRowStyle,
    driverGroupsRowStyle,
    driversRowStyle
  });

  // --- Row heights ---
  const { headerHeight, rowHeight, rowHeightByType } = useRowHeights({
    headerRowStyle,
    normalRowStyle,
    rowStylesPerType,
    selectedForecastsCount: selectedForecasts.length,
    zoom,
    doNotBreak
  });

  // --- Collapsed fields sync ---
  useEffect(() => {
    if (!viewOnly && (collapsedFieldsMemo || []).join('#') !== collapsedFields.join('#')) {
      setCollapsedFieldsMemo(collapsedFields);
    }
  }, [collapsedFields, collapsedFieldsMemo, viewOnly]);

  // --- Pagination: calculate rows per page ---
  useEffect(() => {
    if (!hasData) {
      setFieldsByPage([]);
      return;
    }
    const result = calculatePagination({
      data: data as { fields?: Record<string, unknown>; periods?: unknown[] }[],
      fields,
      allFinFieldsArray,
      collapsedFields: collapsedFieldsMemo,
      headerHeight,
      rowHeight,
      rowHeightByType,
      initialSpace,
      maxSpace,
      breakSpace,
      titlesSize: TITLES_SIZE,
      columnsNumber,
      maxColumnsPerTable,
      zoom,
      doNotBreak
    });
    setFieldsByPage(result.fieldsByPage);
    setTopMargins(result.topMargins);
    setTopPadding(result.topPadding);
  }, [
    hasData,
    initialSpace,
    maxSpace,
    breakSpace,
    fields,
    headerHeight,
    rowHeightByType,
    rowHeight,
    doNotBreak,
    data,
    allFinFieldsArray,
    columnsNumber,
    maxColumnsPerTable,
    zoom,
    collapsedFieldsMemo
  ]);

  // --- Derived data ---
  const firstFinField = useMemo(() => (data.length && data[0]?.fields ? Object.keys(data[0]?.fields)[0] : undefined), [
    data
  ]);
  const forecastsData = useMemo(() => selectedForecasts.map(sf => data.find(d => d?.forecastId === sf.id)), [
    selectedForecasts,
    data
  ]);

  const localizedTrackerForecast = intl.formatMessage({
    id: 'reporting.daily_tracker_forecast',
    defaultMessage: 'Daily tracker forecast'
  });

  // --- Period type classification ---
  const periodsType = useMemo(() => {
    if (!data[0]?.periods?.length) {
      return [];
    }
    const increment = firstFinField
      ? (data[0]?.field_meta[firstFinField]?.column_meta?.length || 0) / data[0].periods.length
      : data[0].periods.length;
    return data[0]?.periods.map((_, i) => {
      const date = firstFinField ? data[0]?.field_meta[firstFinField]?.column_meta[i * increment]?.start_date : '';
      return !date ? '' : isBeforeCurrentPeriod(date, periodType) ? 'Past' : 'Future';
    });
  }, [data, firstFinField, periodType]);

  const periodsTypeByForecast = useMemo(() => {
    if (!data[0]?.periods?.length || !selectedForecasts?.length) {
      return {};
    }
    const result: Record<string, undefined | string[]> = {};
    const increment = firstFinField
      ? (data[0]?.field_meta[firstFinField]?.column_meta?.length || 0) / data[0]!.periods.length
      : data[0]!.periods.length;
    const forecasts =
      (data[0].columns?.length || 0) > selectedForecasts.length
        ? (data[0].columns
            ?.slice(0, selectedForecasts.length)
            .map(
              (cf, i) =>
                selectedForecasts.find(sf => sf.name === (cf === 'Tracker' ? localizedTrackerForecast : cf)) ||
                selectedForecasts[i]
            ) as (ForecastListItem | ConsolidatedForecastListItem)[])
        : selectedForecasts;
    forecasts.forEach((sf, fi) => {
      const forecastPeriodsType = data[0]?.periods.map((_, i) => {
        const date = firstFinField
          ? data[0]?.field_meta[firstFinField]?.column_meta[(fi % increment) + i * increment]?.start_date
          : '';
        return !date
          ? ''
          : isBeforeCurrentPeriodDate(date, sf.forecast_from_date || '', periodType)
          ? 'Past'
          : 'Future';
      });
      result[sf.id] = forecastPeriodsType;
    });
    return result;
  }, [data, firstFinField, selectedForecasts, periodType, localizedTrackerForecast]);

  const periodsTypeByColumn = useMemo(() => {
    if (!hasComparisonData || !data[0]?.periods?.length || !selectedForecasts?.length) {
      return [];
    }
    return data[0]?.columns?.map((columnName, i) => {
      const forecast = selectedForecasts.find(
        f => f.name === (columnName === 'Tracker' ? localizedTrackerForecast : columnName)
      );
      const index = Math.floor(i / ((data[0]?.columns?.length || 1) / (data[0]?.periods.length || 1)));
      return periodsTypeByForecast[forecast?.id || '']?.[index] || periodsType[index];
    });
  }, [periodsTypeByForecast, data, hasComparisonData, periodsType, selectedForecasts, localizedTrackerForecast]);

  // --- Table headers ---
  const { renderTableHeaders } = useTableHeaders({
    data,
    selectedForecasts,
    hasComparisonData,
    firstFinField,
    headerStyles,
    forecastsStyles,
    columnWidth,
    firstColumnWidth,
    maxColumnsPerTable
  });

  // --- Column pagination ---
  useEffect(() => {
    if (!shouldPaginate) {
      setMaxColumnsPerTable(columnsNumber);
    } else {
      if (tableWidth) {
        const columnsPerTable = Math.floor((tableWidth - firstColumnWidth * zoom) / (columnWidth * zoom));
        setMaxColumnsPerTable(Math.min(columnsPerTable, columnsNumber));
        setExtraWidthPerPx(
          (tableWidth - firstColumnWidth * zoom - columnWidth * columnsPerTable * zoom) /
            (firstColumnWidth * zoom + columnWidth * columnsPerTable * zoom)
        );
      }
    }
  }, [columnsNumber, tableWidth, doNotBreak, shouldPaginate, columnWidth, firstColumnWidth, zoom]);

  // --- Build table data ---
  useEffect(() => {
    if (!hasData) {
      setTablesByPage([]);
      return;
    }
    const result = buildTablesByPage({
      fieldsByPage,
      data,
      forecastsData,
      rowsOptions,
      stylesPerType,
      hasComparisonData,
      selectedForecastsLength: selectedForecasts.length,
      firstFinField,
      periodsType,
      periodsTypeByForecast,
      periodsTypeByColumn: periodsTypeByColumn ?? [],
      columnsNumber,
      maxColumnsPerTable,
      currency,
      roundAndFormatValue,
      intl: (intl as unknown) as Record<string, unknown>
    });
    setTablesByPage(result);
  }, [
    fieldsByPage,
    rowsOptions,
    stylesPerType,
    hasComparisonData,
    data,
    forecastsData,
    periodsTypeByForecast,
    periodsType,
    selectedForecasts.length,
    firstFinField,
    intl,
    currency,
    columnsNumber,
    periodsTypeByColumn,
    maxColumnsPerTable,
    hasData,
    roundAndFormatValue
  ]);

  // --- Helpers ---
  const calcTableWidth = useCallback(
    (numOfColumns: number) =>
      firstColumnWidth * (1 + extraWidthPerPx) + numOfColumns * columnWidth * (1 + extraWidthPerPx),
    [extraWidthPerPx, firstColumnWidth, columnWidth]
  );

  const onToggleCollapse = (column: TableColumn) => {
    if (column.collapsible) {
      const newCollapsedFields = collapsedFieldsMemo.includes(column.key)
        ? collapsedFieldsMemo.filter(f => f !== column.key)
        : [...collapsedFieldsMemo, column.key];
      viewOnly ? setCollapsedFieldsMemo(newCollapsedFields) : updateCollapsedFields(newCollapsedFields);
    }
  };

  // --- Render ---
  return (
    <div className={clsx(styles.reportTable)} style={{ paddingTop: topPadding }} ref={tablesContainerRef}>
      {!fieldsByPage?.length && (
        <div key={`table-chunk-empty`} className={clsx(styles.tableChunk)}>
          <div className={clsx(styles.tableHeader)}>
            {!viewOnly ? (
              <EditableTitles title={title} subTitle={subTitle} viewOnly={!!viewOnly} key="table-titles" />
            ) : (
              <>
                <h3>{title}</h3>
                <p>{subTitle}</p>
              </>
            )}
          </div>
        </div>
      )}
      {fieldsByPage?.map((_, pageIndex) => (
        <div
          tabIndex={0}
          key={`table-chunk-${pageIndex}`}
          style={{
            paddingBottom: pageIndex < fieldsByPage.length - 1 ? breakSpace / 2 + topMargins[pageIndex] : 0,
            paddingTop: pageIndex > 0 ? breakSpace / 2 : 0,
            minHeight: pageIndex < fieldsByPage.length - 1 ? (pageIndex === 0 ? initialSpace : maxSpace) : 'auto',
            overflowX: 'auto'
          }}
          className={clsx(styles.tableChunk)}
          aria-label={intl.formatMessage({
            id: 'reporting.scrollable_table_content.aria_label',
            defaultMessage: 'Scrollable table content'
          })}
        >
          <div
            className={clsx(styles.tableHeader)}
            style={{
              paddingTop: pageIndex > 0 ? 5 : 0
            }}
          >
            {fieldsByPage.length > 1 && (
              <span className={clsx(styles.counter)}>
                <FormattedMessage
                  id="reporting.page_of_total_pages"
                  defaultMessage="{pageNumber} of {totalPages}"
                  values={{ pageNumber: pageIndex + 1, totalPages: fieldsByPage.length }}
                />
              </span>
            )}
            {!viewOnly ? (
              <EditableTitles title={title} subTitle={subTitle} viewOnly={!!viewOnly} key={`titles-for-${pageIndex}`} />
            ) : (
              <>
                <h3>{title}</h3>
                <p>{subTitle}</p>
              </>
            )}
          </div>
          {(tablesByPage[pageIndex] || []).map((tableData, ti) => {
            const numberOfColumns = tableData.endColumn - tableData.startColumn;
            return (
              <table
                className={clsx(styles.table)}
                style={{
                  zoom,
                  tableLayout: 'auto',
                  width: numberOfColumns < maxColumnsPerTable ? calcTableWidth(numberOfColumns) : '100%',
                  visibility: tableData.table?.length ? 'visible' : 'hidden'
                }}
                key={`table-chunk-${pageIndex}-${ti}`}
              >
                {renderTableHeaders(tableData.startColumn, tableData.endColumn)}
                <tbody>
                  {tableData.table?.length > 0 &&
                    tableData.table.map((column, ci) => (
                      <tr key={`row-${pageIndex}-${ti}-${column.key}-${ci}`}>
                        <td
                          className={column.cells[0].classes}
                          style={{
                            minWidth: firstColumnWidth,
                            maxWidth: firstColumnWidth,
                            paddingLeft: (!column.collapsible ? 10 : 0) + column.level * 10,
                            cursor: column.collapsible ? 'pointer' : 'default'
                          }}
                          title={column.cells[0].value as string}
                          onClick={() => onToggleCollapse(column)}
                        >
                          {column.collapsible &&
                            (collapsedFieldsMemo.includes(column.key) ? (
                              <Icon type="chevron_right" className={clsx(column.svgClasses)} />
                            ) : (
                              <Icon type="chevron_down" className={clsx(column.svgClasses)} />
                            ))}
                          {column.cells[0].value}
                        </td>
                        {column.cells.slice(tableData.startColumn + 1, tableData.endColumn + 1).map(cell => (
                          <td
                            key={`cell-${pageIndex}-${ti}-${column.key}-${cell.key}`}
                            className={cell.classes}
                            style={{
                              minWidth: columnWidth,
                              maxWidth: columnWidth
                            }}
                          >
                            {cell.value}
                          </td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>
            );
          })}
        </div>
      ))}
    </div>
  );
};
